import { Button, toast } from '@planuze/ui';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCommonContent } from '../content/sections/common.js';

/* Identificador técnico (id/slug/hash) em monospace com botão de copiar. Dado opaco que o staff
   precisa colar em query/ticket/log sem digitar — reusado nos detalhes de publisher e submissão. */

/* Janela (ms) de reset do estado "copiado" — feedback de clipboard, análogo ao code-block. */
const COPIED_RESET_MS = 2000;

interface CopyableIdProps {
  /** Valor a copiar/exibir (identificador técnico, não passa por i18n). */
  readonly value: string;
  /** Rótulo curto do que está sendo copiado, usado no `aria-label` e no toast. */
  readonly label?: string;
}

export function CopyableId({ value, label }: CopyableIdProps) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const copy = useCommonContent().copyableId;

  useEffect(
    () => () => {
      if (resetTimer.current !== undefined) clearTimeout(resetTimer.current);
    },
    [],
  );

  const noun = label ?? copy.defaultNoun;

  function handleCopy(): void {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        toast.success(copy.copiedToast(noun));
        if (resetTimer.current !== undefined) clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
      })
      .catch(() => toast.error(copy.copyErrorToast(noun)));
  }

  return (
    <span className="inline-flex max-w-full items-center gap-1.5">
      <span className="truncate font-mono text-xs text-muted-foreground">{value}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleCopy}
        aria-label={copied ? copy.copiedAriaLabel(noun) : copy.copyAriaLabel(noun)}
        className="size-6 shrink-0 text-muted-foreground hover:bg-accent/15 hover:text-foreground"
      >
        {copied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />}
      </Button>
    </span>
  );
}
